import { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import SearchableSelect from './SearchableSelect';

// Flattens the parent/child relationships into a depth-first, indentable list, same approach as
// MaterialMaster.js's buildItemTypeHierarchy - kept here too since that one is System-Admin-only
// JSX, not an exported/reusable helper.
function buildHierarchy(itemTypes) {
  const byParentId = {};
  itemTypes.forEach((t) => {
    const key = t.parent?.id ?? 'root';
    (byParentId[key] = byParentId[key] || []).push(t);
  });
  const result = [];
  const visit = (parentKey, depth) => {
    (byParentId[parentKey] || [])
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach((t) => {
        result.push({ itemType: t, depth });
        visit(t.id, depth + 1);
      });
  };
  visit('root', 0);
  return result;
}

/**
 * Category picker for Org Admin item-creation forms: an indented dropdown of global
 * (shared) + this org's own categories, plus a "+ New category" affordance that creates an
 * org-scoped one inline via POST /api/organization-item-types, instead of waiting on System Admin.
 */
function CategorySelect({ itemTypes, value, onChange, onCreated, disabled }) {
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newParentId, setNewParentId] = useState('');
  const [saving, setSaving] = useState(false);

  const hierarchy = buildHierarchy(itemTypes);
  const options = hierarchy.map(({ itemType, depth }) => ({
    value: itemType.id,
    label: `${'  '.repeat(depth)}${depth > 0 ? '└─ ' : ''}${itemType.name}`
  }));

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error('Category name is required');
      return;
    }
    setSaving(true);
    try {
      const response = await axios.post('/api/organization-item-types', {
        name: newName.trim(),
        parent: newParentId ? { id: parseInt(newParentId, 10) } : null
      });
      toast.success('Category created');
      setShowNewForm(false);
      setNewName('');
      setNewParentId('');
      onCreated(response.data);
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.response?.data || 'Error creating category';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error creating category');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <SearchableSelect
        options={options}
        value={value}
        onChange={(categoryId) => onChange({ target: { value: categoryId } })}
        disabled={disabled}
        required
        placeholder="Type to search categories…"
        emptyLabel="No matching category"
      />

      {!showNewForm ? (
        <Button variant="link" size="sm" className="p-0 mt-1" onClick={() => setShowNewForm(true)} disabled={disabled}>
          + New category
        </Button>
      ) : (
        <div className="border rounded p-2 mt-2">
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">New category name</Form.Label>
            <Form.Control
              type="text"
              size="sm"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              autoFocus
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label className="small mb-1">Parent (optional)</Form.Label>
            <Form.Select size="sm" value={newParentId} onChange={(e) => setNewParentId(e.target.value)}>
              <option value="">No parent (top-level)</option>
              {hierarchy.map(({ itemType, depth }) => (
                <option key={itemType.id} value={itemType.id}>
                  {'  '.repeat(depth)}
                  {itemType.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <div className="d-flex gap-2">
            <Button size="sm" variant="primary" onClick={handleCreate} disabled={saving}>
              {saving ? 'Creating…' : 'Create category'}
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={() => setShowNewForm(false)} disabled={saving}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CategorySelect;
